export const EMAIL_INVITATION_TYPES = [
    'INVITED_BUYER',
    'INVITED_SUPPLIER',
    'INVITED_COMMUNITY_MEMBER',
] as const;

export type EmailInvitationType = (typeof EMAIL_INVITATION_TYPES)[number];

export type InvitationRequestType = '' | 'CONTACT_REQUEST' | 'EMAIL_INVITATION';

export type InvitationSubTab = 'pending' | 'requested';

export interface EmailInvitationRecord {
    id?: number;
    date?: number | string;
    invitedEmailAddress?: string;
    invitingOrganizationId?: number;
    user?: string;
    invitationState?: string;
    invitationType?: string;
    text?: string;
    customValues?: Record<string, unknown>;
}

export interface InvitationContactRecord {
    id?: number;
    name?: string;
    gln?: string;
    address?: string;
    email?: string;
    date?: number | string;
    acceptanceDate?: number | string;
    organizationId?: number;
    text?: string;
    state?: string;
    invitationType?: string;
    imageUrl?: string;
    customValues?: Record<string, unknown>;
}

export interface ContactRequestRow {
    name?: string;
    address?: string;
    email?: string;
    date?: number | string;
    acceptanceDate?: number | string;
    organizationId?: number;
    user?: string;
    invitationState?: string;
    invitationType?: string;
    invitationText?: string;
    imageUrl?: string;
    requestType: 'CONTACT_REQUEST' | 'EMAIL_INVITATION';
    original: EmailInvitationRecord | InvitationContactRecord;
}

export interface InvitationQuery {
    keyword: string;
    type: InvitationRequestType;
    closedVisible: boolean;
}

const CLOSED_STATES = new Set(['ACCEPTED', 'REJECTED', 'ESTABLISHED']);

export function invitationSubTab(hash: string): InvitationSubTab {
    return hash === 'requested' ? 'requested' : 'pending';
}

export function displayInvitationState(state?: string): string | undefined {
    if (state === 'INVITED') {
        return 'PENDING';
    }
    return state;
}

export function isClosedInvitation(state?: string): boolean {
    return state != null && CLOSED_STATES.has(state);
}

export function canResendInvitation(state?: string): boolean {
    return state !== 'ACCEPTED';
}

export function canEditInvitation(state?: string): boolean {
    return state === 'PENDING';
}

export function statusTitleKey(state?: string): string | undefined {
    if (state === 'PENDING') {
        return 'WAITING_FOR_ACCEPTANCE';
    }
    if (state === 'ACCEPTED' || state === 'ESTABLISHED') {
        return 'INVITATION_ACCEPTED';
    }
    if (state === 'REJECTED') {
        return 'INVITATION_REJECTED';
    }
    return undefined;
}

export function statusBadgeVariant(state?: string): 'info' | 'success' | 'warning' | 'neutral' {
    if (state === 'ACCEPTED' || state === 'ESTABLISHED') {
        return 'success';
    }
    if (state === 'REJECTED') {
        return 'warning';
    }
    if (state === 'PENDING' || state === 'INVITED') {
        return 'info';
    }
    return 'neutral';
}

export function invitationTypeLabelKey(invitationType?: string): string | undefined {
    if (!invitationType) {
        return undefined;
    }
    return `INVITATION.${invitationType}_LABEL`;
}

const timeValue = (value?: number | string): number => {
    if (value == null || value === '') {
        return 0;
    }
    if (typeof value === 'number') {
        return value;
    }
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
};

const matchesKeyword = (value: unknown, keyword: string): boolean => {
    const needle = keyword.trim().toLowerCase();
    if (!needle) {
        return true;
    }
    const visit = (node: unknown): boolean => {
        if (node == null) {
            return false;
        }
        if (typeof node === 'string' || typeof node === 'number') {
            return String(node).toLowerCase().includes(needle);
        }
        if (Array.isArray(node)) {
            return node.some(visit);
        }
        if (typeof node === 'object') {
            return Object.values(node).some(visit);
        }
        return false;
    };
    return visit(value);
};

export function mergeContactRequests(
    emails: EmailInvitationRecord[],
    contacts: InvitationContactRecord[],
    userName: (userId?: string) => string,
): ContactRequestRow[] {
    const emailRows: ContactRequestRow[] = emails.map((contact) => ({
        date: contact.date,
        email: contact.invitedEmailAddress,
        organizationId: contact.invitingOrganizationId,
        user: userName(contact.user),
        invitationState: displayInvitationState(contact.invitationState),
        invitationType: contact.invitationType,
        invitationText: contact.text,
        requestType: 'EMAIL_INVITATION',
        original: contact,
    }));
    const contactRows: ContactRequestRow[] = contacts.map((contact) => ({
        name: contact.name,
        address: contact.address,
        email: contact.email,
        date: contact.date,
        acceptanceDate: contact.acceptanceDate,
        organizationId: contact.organizationId,
        invitationText: contact.text,
        invitationState: displayInvitationState(contact.state),
        invitationType: contact.invitationType,
        imageUrl: contact.imageUrl,
        requestType: 'CONTACT_REQUEST',
        original: contact,
    }));
    return [...emailRows, ...contactRows].sort((left, right) => {
        const dateDiff = timeValue(right.date) - timeValue(left.date);
        if (dateDiff !== 0) {
            return dateDiff;
        }
        return (left.invitationType ?? '').localeCompare(right.invitationType ?? '');
    });
}

export function filterContactRequests(rows: ContactRequestRow[], query: InvitationQuery): ContactRequestRow[] {
    return rows.filter((row) => {
        if (query.type && row.requestType !== query.type) {
            return false;
        }
        if (isClosedInvitation(row.invitationState) && !query.closedVisible) {
            return false;
        }
        return matchesKeyword(row, query.keyword);
    });
}

export function storedInvitationType(): EmailInvitationType {
    try {
        const stored = window.localStorage.getItem('invitationType');
        if (stored && EMAIL_INVITATION_TYPES.includes(stored as EmailInvitationType)) {
            return stored as EmailInvitationType;
        }
    } catch {
        // Storage can be blocked; the dialog still opens with the default type.
    }
    return 'INVITED_SUPPLIER';
}

export function rememberInvitationType(invitationType?: string) {
    if (!invitationType) {
        return;
    }
    try {
        window.localStorage.setItem('invitationType', invitationType);
    } catch {
        // Remembering the last type is a convenience, not required to send.
    }
}
